import { TOUR_ATTRIBUTE } from "../runtime/constants";

/**
 * Targets the app marks with a `data-tour` attribute. The attribute value is
 * derived from the key, so a target has exactly one name: add the key here and
 * spread `tourAttr("<key>")` on the element.
 */
const ATTR_TARGET_KEYS = [
  "shell.createRequest",

  "request.stepper",
  "request.upload",
  "request.chooseTemplate",
  "request.fromCloud",
  "request.recipientsAdd",
  "request.recipientTabs",
  "request.recipientRow",
  "request.participantRail",
  "request.pageContextMenu",
  /**
   * Not spotlighted on its own — it scopes the `request.tab*` selectors below,
   * so the marker has to stay on the rail even though no step targets it.
   */
  "request.rightRail",

  "bulk.signersBar",
  "bulk.importCsv",
  "bulk.addSigner",

  "webforms.create",
  "webforms.list",

  "inbox.tabs",
  "inbox.list",

  "viewRequest.documents",
  "viewRequest.signers",
  "viewRequest.audit",

  "dashboard.dropzone",
  "dashboard.recentActivity",
  "dashboard.renewals",

  "requests.createFolder",
  "requests.createRequest",
  "requests.filter",
  "requests.list",

  "insights.chart",
  "insights.totalProgress",
  "insights.requestStatus",

  "reports.tabs",
  "reports.content",

  "templates.ready",
  "templates.header",
  "templates.create",
  "templates.content",

  "contacts.ready",
  "contacts.addBtn",
  "contacts.bulkUpload",
  "contacts.list",
  "contacts.details",
] as const;

export type TourAttrTargetKey = (typeof ATTR_TARGET_KEYS)[number];

/** `"request.recipientRow"` becomes `"request-recipient-row"`. */
export const attrNameFor = (key: TourAttrTargetKey): string =>
  key.replace(/\./g, "-").replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`);

const attrSelector = (key: TourAttrTargetKey): string =>
  `[${TOUR_ATTRIBUTE}="${attrNameFor(key)}"]`;

const ATTR_SELECTORS = ATTR_TARGET_KEYS.reduce(
  (selectors, key) => {
    selectors[key] = attrSelector(key);
    return selectors;
  },
  {} as Record<TourAttrTargetKey, string>,
);

const RIGHT_RAIL = ATTR_SELECTORS["request.rightRail"];

/** Targets already addressable by an id or ARIA attribute — no markup added. */
const CSS_TARGETS = {
  "shell.inbox": 'button[aria-haspopup="dialog"][aria-label^="Inbox"]',

  /** Existing id on the "Set Signer's order" toggle. */
  "request.signerOrder": "#add-signers-order-checkbox",

  "request.emailContent": "#email-config-section-email-content",
  "request.emailContentHead":
    '[aria-controls="email-config-section-email-content"]',
  "request.reminders": "#email-config-section-reminders-expiration",
  "request.remindersHead":
    '[aria-controls="email-config-section-reminders-expiration"]',
  "request.permissions": "#email-config-section-signer-permissions",
  "request.permissionsHead":
    '[aria-controls="email-config-section-signer-permissions"]',
  "request.schedule": "#email-config-section-schedule-sign-request",
  "request.scheduleHead":
    '[aria-controls="email-config-section-schedule-sign-request"]',

  "request.fieldsPanel": "#draggable-fields",
  "request.document": "#pdf_box",
  /** A rendered page inside the viewer — the element that owns the
   *  right-click handler, which `request.document` only contains. */
  "request.documentPage": ".react-pdf__Page",

  "request.tabDocuments": `${RIGHT_RAIL} button[aria-label="Documents"]`,
  "request.tabProperties": `${RIGHT_RAIL} button[aria-label="Field properties"]`,
  "request.tabAssistant": `${RIGHT_RAIL} button[aria-label="AI Assistant"]`,
  "request.tabInsights": `${RIGHT_RAIL} button[aria-label="AI Insights"]`,

  "insights.workflowTab": "#insights-tab-workflow",
  "insights.distributionTab": "#insights-tab-distribution",

  "viewRequest.actions": 'button[aria-label="Request options"]',
} as const;

export type TourTargetKey = TourAttrTargetKey | keyof typeof CSS_TARGETS;

export const TOUR_TARGETS: Record<TourTargetKey, string> = {
  ...ATTR_SELECTORS,
  ...CSS_TARGETS,
};
